import { useEffect, useState } from 'react';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { isNativeAppShell } from '../utils/mobileView';

function setKeyboardInset(px) {
  if (typeof document === 'undefined') return;
  const safeHeight = Math.max(0, Math.round(px));
  // Keep the keyboard height out of CSS layout. iOS Safari/WebView resize the
  // visual viewport during keyboard animation; using that value in padding or
  // max-height makes the whole shell jump. The hook keeps the measured height
  // in JS and uses it only for targeted scroll-into-view.
  document.documentElement.style.setProperty('--mobile-keyboard-height', '0px');
  document.documentElement.classList.toggle('mobile-keyboard-open', safeHeight > 0);
  document.body.classList.toggle('mobile-keyboard-open', safeHeight > 0);
}

function readVisualViewportKeyboardHeight() {
  if (typeof window === 'undefined' || !window.visualViewport) return 0;
  const viewport = window.visualViewport;
  return Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
}

/** iOS shifts the layout viewport when the keyboard opens — keep the shell pinned. */
function lockWindowScrollPosition() {
  if (typeof window === 'undefined') return;
  if (window.scrollX !== 0 || window.scrollY !== 0) {
    window.scrollTo(0, 0);
  }
}

function readFixedHeaderBottom() {
  if (typeof document === 'undefined') return 0;
  const header = document.querySelector('.mobile-app-shell header');
  if (!(header instanceof HTMLElement)) return 0;
  const rect = header.getBoundingClientRect();
  return Math.max(0, Math.round(rect.bottom));
}

function findScrollContainer(target) {
  if (!(target instanceof HTMLElement)) return null;

  const markedScroll = target.closest('[data-mobile-dashboard-scroll]');
  if (markedScroll instanceof HTMLElement) return markedScroll;

  let node = target.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScroll = /(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight;
    if (canScroll) return node;
    node = node.parentElement;
  }

  const modalPaper = target.closest('.MuiDialog-paper, .MuiDrawer-paper');
  if (modalPaper instanceof HTMLElement) return modalPaper;

  const main = target.closest('main');
  if (main instanceof HTMLElement) return main;
  return null;
}

function isInsideModal(target) {
  return target instanceof HTMLElement && Boolean(target.closest('.MuiModal-root, [role="dialog"]'));
}

/** Scroll only enough to keep the field above the keyboard — never center (avoids top gap). */
function scrollFieldAboveKeyboard(target, keyboardInset) {
  const scrollParent = findScrollContainer(target);
  if (!(scrollParent instanceof HTMLElement)) return;

  const targetRect = target.getBoundingClientRect();
  const keyboardTop = window.innerHeight - keyboardInset;
  const scrollParentRect = scrollParent.getBoundingClientRect();
  const fixedHeaderBottom = isInsideModal(target) ? 0 : readFixedHeaderBottom();
  const visibleBottom = isInsideModal(target)
    ? keyboardTop - 16
    : Math.min(scrollParentRect.bottom, keyboardTop) - 16;
  const visibleTop = Math.max(scrollParentRect.top, fixedHeaderBottom) + 16;

  if (targetRect.bottom > visibleBottom) {
    scrollParent.scrollTop += targetRect.bottom - visibleBottom;
  } else if (targetRect.top < visibleTop) {
    scrollParent.scrollTop -= visibleTop - targetRect.top;
  }
}

/**
 * Tracks on-screen keyboard height for mobile shell layouts.
 * Hides bottom chrome overlap and keeps focused fields scrollable.
 */
export default function useMobileKeyboardLayout(enabled = isMobileShellLayout()) {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setKeyboardHeight(0);
      setKeyboardInset(0);
      return undefined;
    }

    let disposed = false;
    const updateHeight = (nextHeight) => {
      if (disposed) return;
      const safeHeight = Math.max(0, Math.round(nextHeight));
      setKeyboardHeight(safeHeight);
      setKeyboardInset(safeHeight);
    };

    let lastKeyboardInset = 0;

    const reconcileFieldScroll = (target, keyboardInset = lastKeyboardInset) => {
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches('input, textarea, select, [contenteditable="true"]')) return;
      lockWindowScrollPosition();
      scrollFieldAboveKeyboard(target, keyboardInset);
    };

    const onFocusIn = (event) => {
      if (lastKeyboardInset > 0) {
        lockWindowScrollPosition();
      }
      if (!(event.target instanceof HTMLElement)) return;
      window.requestAnimationFrame(() => {
        reconcileFieldScroll(event.target, lastKeyboardInset);
      });
    };

    const onVisualViewportChange = () => {
      if (lastKeyboardInset > 0) {
        lockWindowScrollPosition();
      }

      if (!isNativeAppShell()) {
        const nextInset = readVisualViewportKeyboardHeight();
        lastKeyboardInset = nextInset;
        updateHeight(nextInset);
      }

      const active = document.activeElement;
      if (active instanceof HTMLElement && lastKeyboardInset > 0) {
        reconcileFieldScroll(active, lastKeyboardInset);
      }
    };

    document.addEventListener('focusin', onFocusIn, true);
    const onWindowScroll = () => {
      if (lastKeyboardInset > 0) {
        lockWindowScrollPosition();
      }
    };
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    window.visualViewport?.addEventListener('resize', onVisualViewportChange);
    window.visualViewport?.addEventListener('scroll', onVisualViewportChange);

    let keyboardPluginCleanup = null;

    if (isNativeAppShell()) {
      import('@capacitor/keyboard')
        .then(({ Keyboard }) => {
          if (disposed) return;
          const showHandle = Keyboard.addListener('keyboardWillShow', (info) => {
            lockWindowScrollPosition();
            const inset = info?.keyboardHeight ?? readVisualViewportKeyboardHeight();
            lastKeyboardInset = inset;
            updateHeight(inset);
            const active = document.activeElement;
            if (active instanceof HTMLElement) {
              window.requestAnimationFrame(() => {
                reconcileFieldScroll(active, inset);
              });
            }
          });
          const hideHandle = Keyboard.addListener('keyboardWillHide', () => {
            lastKeyboardInset = 0;
            updateHeight(0);
            lockWindowScrollPosition();
          });
          keyboardPluginCleanup = () => {
            showHandle.then((h) => h.remove());
            hideHandle.then((h) => h.remove());
          };
        })
        .catch(() => {
          onVisualViewportChange();
        });
    } else {
      onVisualViewportChange();
    }

    return () => {
      disposed = true;
      document.removeEventListener('focusin', onFocusIn, true);
      window.removeEventListener('scroll', onWindowScroll);
      window.visualViewport?.removeEventListener('resize', onVisualViewportChange);
      window.visualViewport?.removeEventListener('scroll', onVisualViewportChange);
      keyboardPluginCleanup?.();
      setKeyboardInset(0);
      document.documentElement.classList.remove('mobile-keyboard-open');
      document.body.classList.remove('mobile-keyboard-open');
    };
  }, [enabled]);

  return {
    keyboardHeight,
    keyboardOpen: keyboardHeight > 0,
  };
}
